/**
 * Configured auth-client runtime per RFC-002.
 *
 * Consumers should import the configured singleton:
 *
 *   import { authApi } from "@workspace/auth-client";
 *   const { data: profile } = await authApi.v1.getProfile();
 *
 * The runtime wires auth refresh (with the auth-API's specific skiplist),
 * dedup, and the production base URL so apps don't reinvent any of it. For
 * dev/test overrides, call the factory:
 *
 *   import { createAuthApi } from "@workspace/auth-client";
 *   const authApi = createAuthApi({ baseUrl: "http://localhost:11112" });
 *
 * Calling `createAuthApi()` with no args returns the shared singleton, so
 * `import { authApi }` and `createAuthApi()` give you the same instance.
 *
 * Edge cases construct `new Api({ ... })` from the raw exported class.
 */
import { createAuthFetch } from "@workspace/network";
import { Api } from "./Api.js";
// The package's default is PROD, per the API-client convention ("production
// base URL is hardcoded in the package, env var as override"). It used to fall
// back to the local-estate URL instead, which meant a prod build with the env
// var set still carried that fallback in its bytes as the dead right-hand side
// of the `||` — harmless at runtime, but scripts/check-stage-clean.ts greps
// built output and cannot tell a live origin from an unreachable one, so the
// stage build failed. (Keep local hostnames out of this file for that reason.)
// Override for local/dev via NEXT_PUBLIC_AUTH_API_URL, which build-stage.ts
// injects per stage, or with `createAuthApi({ baseUrl })`.
const PROD_BASE_URL = process.env.NEXT_PUBLIC_AUTH_API_URL || "https://api.auth.gaz.codes";
/**
 * Endpoints where 401 is an expected error (login/register etc.) — refresh
 * makes no sense and would just create an endless loop.
 *
 * Exported so apps that build their own `createAuthFetch` for non-generated
 * endpoints (e.g. PAT management, file bulk ops) can reuse the same skiplist
 * rather than maintaining a duplicate.
 */
export const DEFAULT_NO_REFRESH_PATHS = [
    "/v1/login",
    "/v1/register",
    "/v1/mfa/challenge",
    "/v1/refresh",
    "/v1/magic-link",
    "/v1/password/reset",
    "/v1/verify-email",
    "/v1/webauthn/authenticate",
];
let _default = null;
export function createAuthApi(config) {
    if (!config) {
        if (!_default)
            _default = construct({});
        return _default;
    }
    return construct(config);
}
function construct(config) {
    const envBaseUrl = typeof process !== "undefined"
        ? process.env?.NEXT_PUBLIC_AUTH_API_URL
        : undefined;
    const baseUrl = config.baseUrl ?? envBaseUrl ?? PROD_BASE_URL;
    const noRefreshPaths = config.noRefreshPaths ?? DEFAULT_NO_REFRESH_PATHS;
    const customFetch = config.customFetch ?? createAuthFetch({
        authApiUrl: baseUrl,
        noRefreshPaths,
    });
    return new Api({ baseUrl, customFetch });
}
export const authApi = createAuthApi();
export { Api };
